/**
 * Inline GraphQL documents for the Mediathek admin (MediaSeries / MediaItem,
 * see amplify/backend/api/netfair/schema/mediathek.graphql) plus a small
 * nextToken pagination helper.
 *
 * Only inline documents are used here on purpose: the generated
 * src/graphql/*.js files select every field (incl. nested relations) and are
 * refreshed separately by `amplify codegen`.
 */
import { slugLookupQuery } from '../../utils/slugInput';

const MULTI_LANG = '{ language text }';
const IMAGE = '{ fileName alt x4UUID }';
const FILE = '{ url publicUrl s3Path fileName type name }';

// ---------------------------------------------------------------------------
// Field selections
// ---------------------------------------------------------------------------

const SERIES_FIELDS = `
      id
      type
      title ${MULTI_LANG}
      slug
      slugHistory
      excerpt ${MULTI_LANG}
      description ${MULTI_LANG}
      host
      coverImage ${IMAGE}
      status
      sortKey
      industryId
      legacyKey
      legacyData
      createdAt
      updatedAt`;

const SERIES_LIST_FIELDS = `
      id
      type
      title ${MULTI_LANG}
      slug
      host
      coverImage ${IMAGE}
      status
      sortKey
      industryId
      createdAt
      updatedAt`;

const ITEM_FIELDS = `
      id
      mediaSeriesId
      title ${MULTI_LANG}
      slug
      slugHistory
      excerpt ${MULTI_LANG}
      content ${MULTI_LANG}
      episodeNumber
      topic
      publishedAt
      isHero
      status
      sortKey
      guest
      guestEmail
      guestPhone
      host
      speaker
      companyName
      companyUrl
      companyId
      image ${IMAGE}
      audio ${FILE}
      video ${FILE}
      externalVideoUrl
      spotifyUrl
      podigeeUrl
      legacyKey
      legacyData
      createdAt
      updatedAt`;

const ITEM_LIST_FIELDS = `
      id
      mediaSeriesId
      title ${MULTI_LANG}
      slug
      episodeNumber
      publishedAt
      isHero
      status
      sortKey
      guest
      companyName
      image { fileName }
      audio { s3Path }
      video { s3Path }
      externalVideoUrl
      createdAt
      updatedAt`;

// ---------------------------------------------------------------------------
// MediaSeries
// ---------------------------------------------------------------------------

/** Series overview incl. the ids/status of their items (first page; see countSeriesItems for more). */
export const LIST_MEDIA_SERIES = /* GraphQL */ `
  query ListMediaSeries($filter: ModelMediaSeriesFilterInput, $limit: Int, $nextToken: String) {
    listMediaSeries(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {${SERIES_LIST_FIELDS}
        items(limit: 1000) {
          items {
            id
            status
          }
          nextToken
        }
      }
      nextToken
    }
  }
`;

/** Series for select boxes / lookups (no nested items). */
export const LIST_MEDIA_SERIES_OPTIONS = /* GraphQL */ `
  query ListMediaSeriesOptions($filter: ModelMediaSeriesFilterInput, $limit: Int, $nextToken: String) {
    listMediaSeries(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        type
        title ${MULTI_LANG}
        slug
        status
        sortKey
      }
      nextToken
    }
  }
`;

export const GET_MEDIA_SERIES = /* GraphQL */ `
  query GetMediaSeries($id: ID!) {
    getMediaSeries(id: $id) {${SERIES_FIELDS}
    }
  }
`;

export const CREATE_MEDIA_SERIES = /* GraphQL */ `
  mutation CreateMediaSeries($input: CreateMediaSeriesInput!) {
    createMediaSeries(input: $input) {
      id
      slug
      slugHistory
    }
  }
`;

export const UPDATE_MEDIA_SERIES = /* GraphQL */ `
  mutation UpdateMediaSeries($input: UpdateMediaSeriesInput!) {
    updateMediaSeries(input: $input) {
      id
      slug
      slugHistory
    }
  }
`;

export const DELETE_MEDIA_SERIES = /* GraphQL */ `
  mutation DeleteMediaSeries($input: DeleteMediaSeriesInput!) {
    deleteMediaSeries(input: $input) {
      id
    }
  }
`;

// ---------------------------------------------------------------------------
// MediaItem
// ---------------------------------------------------------------------------

export const LIST_MEDIA_ITEMS = /* GraphQL */ `
  query ListMediaItems($filter: ModelMediaItemFilterInput, $limit: Int, $nextToken: String) {
    listMediaItems(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {${ITEM_LIST_FIELDS}
      }
      nextToken
    }
  }
`;

export const MEDIA_ITEMS_BY_MEDIA_SERIES = /* GraphQL */ `
  query MediaItemsByMediaSeries($mediaSeriesId: ID!, $filter: ModelMediaItemFilterInput, $limit: Int, $nextToken: String) {
    mediaItemsByMediaSeries(mediaSeriesId: $mediaSeriesId, filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {${ITEM_LIST_FIELDS}
      }
      nextToken
    }
  }
`;

/** Only ids - for counting / "does the series still have items" checks. */
export const MEDIA_ITEM_IDS_BY_MEDIA_SERIES = /* GraphQL */ `
  query MediaItemIdsByMediaSeries($mediaSeriesId: ID!, $limit: Int, $nextToken: String) {
    mediaItemsByMediaSeries(mediaSeriesId: $mediaSeriesId, limit: $limit, nextToken: $nextToken) {
      items {
        id
        status
      }
      nextToken
    }
  }
`;

export const GET_MEDIA_ITEM = /* GraphQL */ `
  query GetMediaItem($id: ID!) {
    getMediaItem(id: $id) {${ITEM_FIELDS}
    }
  }
`;

export const CREATE_MEDIA_ITEM = /* GraphQL */ `
  mutation CreateMediaItem($input: CreateMediaItemInput!) {
    createMediaItem(input: $input) {
      id
      mediaSeriesId
      slug
      slugHistory
    }
  }
`;

export const UPDATE_MEDIA_ITEM = /* GraphQL */ `
  mutation UpdateMediaItem($input: UpdateMediaItemInput!) {
    updateMediaItem(input: $input) {
      id
      mediaSeriesId
      slug
      slugHistory
    }
  }
`;

/** Drag-reorder: only { id, sortKey } is sent. */
export const UPDATE_MEDIA_ITEM_SORT_KEY = /* GraphQL */ `
  mutation UpdateMediaItemSortKey($input: UpdateMediaItemInput!) {
    updateMediaItem(input: $input) {
      id
      sortKey
    }
  }
`;

export const DELETE_MEDIA_ITEM = /* GraphQL */ `
  mutation DeleteMediaItem($input: DeleteMediaItemInput!) {
    deleteMediaItem(input: $input) {
      id
    }
  }
`;

// ---------------------------------------------------------------------------
// Slug lookups (same documents as the SlugField uses: mediaSeriesBySlug / mediaItemsBySlug)
// ---------------------------------------------------------------------------

export const MEDIA_SERIES_BY_SLUG = slugLookupQuery('mediaSeries');
export const MEDIA_ITEMS_BY_SLUG = slugLookupQuery('mediaItem');

// ---------------------------------------------------------------------------
// Other models / mutations
// ---------------------------------------------------------------------------

export const LIST_INDUSTRIES = /* GraphQL */ `
  query ListIndustries($limit: Int, $nextToken: String) {
    listIndustries(limit: $limit, nextToken: $nextToken) {
      items {
        id
        name ${MULTI_LANG}
        handle
      }
      nextToken
    }
  }
`;

export const SEARCH_COMPANIES = /* GraphQL */ `
  query SearchCompanies($filter: SearchableCompanyFilterInput, $limit: Int) {
    searchCompanies(filter: $filter, limit: $limit) {
      items {
        id
        name
        image {
          main {
            fileName
          }
        }
        status
      }
    }
  }
`;

export const GET_COMPANY_NAME = /* GraphQL */ `
  query GetCompanyName($id: ID!) {
    getCompany(id: $id) {
      id
      name
    }
  }
`;

export const UPLOAD_COMPANY_IMAGE = /* GraphQL */ `
  mutation UploadCompanyImage($imageBuffer: String, $imageType: String) {
    uploadCompanyImage(imageBuffer: $imageBuffer, imageType: $imageType)
  }
`;

export const FINALIZE_MEDIATHEK_UPLOAD = /* GraphQL */ `
  mutation FinalizeMediathekUpload($input: MediathekUploadInput!) {
    finalizeMediathekUpload(input: $input) {
      s3Path
      url
      fileName
      type
      size
    }
  }
`;

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------

/**
 * Runs a list query until nextToken is exhausted and returns all items.
 * @param {object} client generateClient() instance
 * @param {string} query GraphQL document
 * @param {object} variables variables without nextToken
 * @param {string} field root field of the connection, e.g. "listMediaItems"
 * @param {number} [limit=1000] page size
 */
export async function fetchAllPages(client, query, variables, field, limit = 1000) {
  const all = [];
  let nextToken = null;
  do {
    const response = await client.graphql({ query, variables: { ...variables, limit, nextToken } });
    const connection = response?.data?.[field];
    all.push(...(connection?.items || []).filter(Boolean));
    nextToken = connection?.nextToken || null;
  } while (nextToken);
  return all;
}
